import { Component, Inject, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatSnackBar } from '@angular/material/snack-bar';
import { OrderService } from '../../../order.service';
import { LinkedGiftCard } from 'src/app/models/gift-card';
import { CardDetails } from 'src/app/models/order';
import { PaymentComponent as AddPaymentMethodComponent } from 'src/app/payment/payment.component';
import { PaymentStripeComponent } from 'src/app/payment-stripe/payment-stripe.component';
import { PaymentGatewayComponent } from 'src/app/payment-gateway/payment-gateway.component';

@Component({
  selector: 'app-refill-gift-card-dialog',
  templateUrl: './refill-gift-card-dialog.component.html',
  styleUrls: ['./refill-gift-card-dialog.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RefillGiftCardDialogComponent implements OnInit {
  card: LinkedGiftCard;
  storeId: string;
  phoneNumber: string;
  paymentGateway: string;
  store: any;

  savedCards: CardDetails[] = [];
  selectedCard: CardDetails = null;
  selectedAmount: number = null;
  customAmount: string = '';
  isCustom: boolean = false;

  // Set by backend after create-order call
  orderHash: string = null;
  serviceCharge: number = 0;
  totalCharge: number = 0;

  canAddCard: boolean = false;
  isLoadingCards: boolean = true;
  isCreatingOrder: boolean = false;
  isProcessing: boolean = false;
  error: string = '';

  // Step 1 = select amount/card, Step 2 = confirm & pay
  step: number = 1;

  presetAmounts: number[] = [10, 15, 20, 25, 30, 50];

  constructor(
    private dialogRef: MatDialogRef<RefillGiftCardDialogComponent>,
    private orderService: OrderService,
    private snackBar: MatSnackBar,
    private bottomSheet: MatBottomSheet,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private cdr: ChangeDetectorRef
  ) {
    this.card = data.card;
    this.storeId = data.storeId;
    this.phoneNumber = data.phoneNumber;
    this.paymentGateway = data.paymentGateway;
    this.store = data.store;
    this.canAddCard = ['STRIPE', 'CARDCONNECT'].includes(this.paymentGateway);
  }

  ngOnInit() {
    this.loadSavedCards();
  }

  loadSavedCards() {
    this.isLoadingCards = true;
    this.orderService.getCards(this.storeId, this.phoneNumber, this.paymentGateway).subscribe(
      (response: any) => {
        this.savedCards = (response.cards || []).filter((c: CardDetails) => !c.is_deleted);
        this.isLoadingCards = false;
        if (this.savedCards.length === 1) {
          this.selectedCard = this.savedCards[0];
        }
        this.cdr.markForCheck();
      },
      (error) => {
        this.isLoadingCards = false;
        this.error = 'Failed to load payment methods.';
        this.cdr.markForCheck();
      }
    );
  }

  selectAmount(amount: number) {
    this.selectedAmount = amount;
    this.isCustom = false;
    this.customAmount = '';
  }

  enableCustomAmount() {
    this.isCustom = true;
    this.selectedAmount = null;
    this.customAmount = '';
  }

  onCustomAmountChange() {
    const parsed = parseFloat(this.customAmount);
    if (!isNaN(parsed) && parsed > 0) {
      this.selectedAmount = parsed;
    } else {
      this.selectedAmount = null;
    }
  }

  selectCard(card: CardDetails) {
    this.selectedCard = card;
  }

  canProceed(): boolean {
    return this.selectedAmount > 0 && this.selectedCard !== null && !this.isCreatingOrder;
  }

  // Step 1: Create refill order — backend returns service charge + total
  createOrder() {
    if (!this.canProceed()) return;

    this.isCreatingOrder = true;
    this.error = '';

    this.orderService.createRefillOrder(
      this.card.card_number,
      this.phoneNumber,
      this.storeId,
      this.selectedAmount
    ).subscribe(
      (response: any) => {
        this.orderHash = response.order_hash;
        this.serviceCharge = parseFloat(response.service_charge) || 0;
        this.totalCharge = parseFloat(response.total) || 0;
        this.isCreatingOrder = false;
        this.step = 2;
        this.cdr.markForCheck();
      },
      (error) => {
        this.isCreatingOrder = false;
        if (error.error && error.error.detail) {
          this.error = error.error.detail;
        } else {
          this.error = 'Failed to create refill order. Please try again.';
        }
        this.cdr.markForCheck();
      }
    );
  }

  // Step 2: Submit payment using order_hash + selected card
  submitPayment() {
    if (!this.orderHash || !this.selectedCard || this.isProcessing) return;

    this.isProcessing = true;
    this.error = '';

    this.orderService.submitRefillPayment(
      this.orderHash,
      this.selectedCard.id
    ).subscribe(
      (response: any) => {
        this.isProcessing = false;
        this.snackBar.open('Balance added successfully!', '', {
          duration: 3000,
          verticalPosition: 'top'
        });
        this.cdr.markForCheck();
        this.dialogRef.close(true);
      },
      (error) => {
        this.isProcessing = false;
        if (error.error && error.error.detail) {
          this.error = error.error.detail;
        } else {
          this.error = 'Payment failed. Please try again.';
        }
        this.cdr.markForCheck();
      }
    );
  }

  goBackToStep1() {
    this.step = 1;
    this.orderHash = null;
    this.serviceCharge = 0;
    this.totalCharge = 0;
  }

  addNewCard() {
    const payload = {
      storeId: this.storeId,
      phoneNumber: this.phoneNumber,
      paymentGateway: this.paymentGateway,
      store: this.store
    };

    let paymentRef;
    if (this.paymentGateway === 'STRIPE') {
      paymentRef = this.bottomSheet.open(PaymentStripeComponent, {
        panelClass: 'payment-bottom-sheet-container',
        data: payload
      });
    } else if (this.paymentGateway === 'CARDCONNECT') {
      paymentRef = this.bottomSheet.open(AddPaymentMethodComponent, {
        panelClass: 'payment-bottom-sheet-container',
        data: payload
      });
    } else {
      paymentRef = this.bottomSheet.open(PaymentGatewayComponent, {
        panelClass: 'payment-bottom-sheet-container',
        data: payload
      });
    }

    if (paymentRef) {
      paymentRef.afterDismissed().subscribe((cardDetail: CardDetails) => {
        if (cardDetail) {
          this.savedCards.push(cardDetail);
          this.selectedCard = cardDetail;
          this.cdr.markForCheck();
        }
      });
    }
  }

  dismiss() {
    this.dialogRef.close(false);
  }

  getMaskedCardNumber(cardNumber: string): string {
    if (cardNumber && cardNumber.length >= 4) {
      return '****' + cardNumber.slice(-4);
    }
    return cardNumber || '';
  }
}
